import { SearchInput } from '@euler/app/components/SearchInput';
import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import { showErpToast } from '@euler/app/flows/erp/utils/showErpToast';
import { Button } from '@euler/components/Button';
import { Colors, StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { SimpleOrgMember } from '@euler/model';
import { ErpIntegrationType } from '@euler/model/enum';
import {
  ErpConnectionEmployeeMappings,
  ErpConnectionInfo,
  ErpEmployee,
} from '@euler/model/erp-integrations';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  Cell2 as Cell,
  Section,
} from '../../settings/components/SettingsTable';

export const ErpEmployeeMappingScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  {
    title: '员工绑定',
  },
);

const Content = memo(() => {
  const route = useRoute<any>();
  const providerIdFromRoute = route.params?.providerId;

  const { storeErpIntegrationService, storeService } = useServiceFactory();
  const { getProviderName } = useErpProviders();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connection, setConnection] = useState<
    ErpConnectionInfo | null | undefined
  >(undefined);
  const [erpEmployees, setErpEmployees] = useState<ErpEmployee[]>([]);
  const [storeMembers, setStoreMembers] = useState<SimpleOrgMember[]>([]);
  const [mappings, setMappings] = useState<ErpConnectionEmployeeMappings>({
    erpProviderId: providerIdFromRoute,
    done: false,
    items: [],
  });
  const [selectorMemberId, setSelectorMemberId] = useState<number | undefined>(
    undefined,
  );
  const [memberQuery, setMemberQuery] = useState('');
  const [erpEmployeeQuery, setErpEmployeeQuery] = useState('');
  const [dirty, setDirty] = useState(false);

  const currentProviderId = connection?.erpProviderId ?? providerIdFromRoute;
  const currentProviderName = getProviderName(currentProviderId, '当前服务商');

  const navigation = useNavigation<any>();
  const confirm = useConfirm();

  useEffect(() => {
    navigation.setOptions({
      title: currentProviderId ? `${currentProviderName}员工绑定` : '员工绑定',
    });
  }, [currentProviderId, currentProviderName, navigation]);

  useEffect(() => {
    return navigation.addListener('beforeRemove', (e: any) => {
      if (!dirty) {
        return;
      }
      e.preventDefault();
      void confirm({
        title: '离开前确认',
        message: '员工绑定关系尚未保存，离开后本次调整将丢失，确定返回吗？',
        confirmButtonText: '确认离开',
        cancelButtonText: '继续编辑',
      }).then(confirmed => {
        if (confirmed) {
          navigation.dispatch(e.data.action);
        }
      });
    });
  }, [confirm, dirty, navigation]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // The mapping belongs to the connection, but listing the ERP's staff
      // goes through a hosted integration — that is the only mechanism whose
      // session we hold. Without one there is nobody to ask.
      const { connections } =
        await storeErpIntegrationService.getErpConnections();
      const liveConnection = connections.find(
        x => x.activeIntegrationCount > 0,
      );
      const hosted = liveConnection?.activeIntegrations.find(
        x => x.type === ErpIntegrationType.Hosted,
      );
      setConnection(liveConnection);

      const nextProviderId =
        liveConnection?.erpProviderId ?? providerIdFromRoute;
      if (!nextProviderId || !hosted) {
        setErpEmployees([]);
        setStoreMembers([]);
        setMappings({
          erpProviderId: providerIdFromRoute,
          done: false,
          items: [],
        });
        setDirty(false);
        return;
      }

      const [employees, membersRes, mappingsRes] = await Promise.all([
        storeErpIntegrationService.getErpEmployees(hosted.id).catch(async e => {
          // By subCode, not message: the message is localized copy.
          if (e.subCode === 'erp_auth_expired') {
            showErpToast(
              `${getProviderName(nextProviderId)}登录已失效，请先重新登录授权`,
              'error',
            );
            navigation.goBack();
          } else {
            console.error('getErpEmployees error:', e);
          }
          return [];
        }),
        storeService.getSimpleMemberList().catch(e => {
          console.warn('getSimpleMemberList error:', e);
          return [];
        }),
        storeErpIntegrationService
          .getBrowserExtensionErpIntegrationEmployeeMappings()
          .catch(() => ({
            erpProviderId: nextProviderId,
            done: false,
            items: [],
          })),
      ]);

      setErpEmployees(
        [...(employees || [])].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setStoreMembers(
        [...(membersRes || [])].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setMappings(
        mappingsRes || {
          erpProviderId: nextProviderId,
          done: false,
          items: [],
        },
      );
      setDirty(false);
    } catch (e: any) {
      console.warn(e);
      showErpToast('数据加载失败: ' + (e.message || '未知错误'), 'error');
    } finally {
      setLoading(false);
    }
  }, [
    getProviderName,
    navigation,
    providerIdFromRoute,
    storeErpIntegrationService,
    storeService,
  ]);

  useFocusEffect(
    useCallback(() => {
      void fetchData();
    }, [fetchData]),
  );

  const onSave = usePersistFn(async () => {
    if (!currentProviderId) {
      showErpToast('请先绑定服务商', 'error');
      return;
    }
    try {
      setSaving(true);
      await storeErpIntegrationService.saveBrowserExtensionErpIntegrationEmployeeMappings(
        {
          ...mappings,
          erpProviderId: currentProviderId,
          // Saving is the only moment the store states where it stands, and
          // it is finished exactly when nothing is left pending — the same
          // 待处理 count shown above the list.
          done: (mappings.items?.length ?? 0) >= storeMembers.length,
        },
      );
      showErpToast('保存成功', 'success');
      setDirty(false);
    } catch (e: any) {
      showErpToast('保存失败: ' + (e.message || '未知错误'), 'error');
    } finally {
      setSaving(false);
    }
  });

  const updateMapping = usePersistFn(
    async (memberId: number, erpEmployeeId: string | null) => {
      if (erpEmployeeId) {
        const conflictMember = storeMembers.find(member => {
          if (member.id === memberId) {
            return false;
          }
          return mappings.items?.some(
            item =>
              item.memberId === member.id &&
              item.erpEmployeeId === erpEmployeeId,
          );
        });

        if (conflictMember) {
          const confirmed = await confirm({
            title: '确认替换绑定',
            message: `该${currentProviderName}员工已绑定给 ${conflictMember.name}，继续将解除原绑定。`,
            confirmButtonText: '继续',
            cancelButtonText: '取消',
          });
          if (!confirmed) {
            return;
          }
        }
      }

      setMappings(prev => {
        const nextItems = (prev.items || []).filter(item => {
          if (item.memberId === memberId) {
            return false;
          }
          if (erpEmployeeId && item.erpEmployeeId === erpEmployeeId) {
            return false;
          }
          return true;
        });

        if (erpEmployeeId) {
          nextItems.push({ memberId, erpEmployeeId });
        }

        return {
          ...prev,
          erpProviderId: currentProviderId,
          items: nextItems,
        };
      });
      setDirty(true);
      setSelectorMemberId(undefined);
      setErpEmployeeQuery('');
    },
  );

  const selectedMember = useMemo(() => {
    return storeMembers.find(member => member.id === selectorMemberId);
  }, [selectorMemberId, storeMembers]);

  const selectedMemberMapping = useMemo(() => {
    return mappings.items?.find(item => item.memberId === selectorMemberId);
  }, [mappings.items, selectorMemberId]);

  const getBoundEmployee = useCallback(
    (memberId: number) => {
      const mapping = mappings.items?.find(item => item.memberId === memberId);
      if (!mapping) {
        return undefined;
      }
      return erpEmployees.find(
        employee => employee.id === mapping.erpEmployeeId,
      );
    },
    [erpEmployees, mappings.items],
  );

  const getBindingMemberName = useCallback(
    (erpEmployeeId: string) => {
      const mapping = mappings.items?.find(
        item => item.erpEmployeeId === erpEmployeeId,
      );
      if (!mapping) {
        return undefined;
      }
      return storeMembers.find(member => member.id === mapping.memberId)?.name;
    },
    [mappings.items, storeMembers],
  );

  const filteredStoreMembers = useMemo(() => {
    const keyword = memberQuery.trim().toLowerCase();
    if (!keyword) {
      return storeMembers;
    }
    return storeMembers.filter(member => {
      const boundEmployee = getBoundEmployee(member.id);
      const searchText = [
        member.name,
        boundEmployee?.name,
        boundEmployee?.mobile,
        boundEmployee?.staffno,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchText.includes(keyword);
    });
  }, [getBoundEmployee, memberQuery, storeMembers]);

  const filteredErpEmployees = useMemo(() => {
    const keyword = erpEmployeeQuery.trim().toLowerCase();
    if (!keyword) {
      return erpEmployees;
    }
    return erpEmployees.filter(employee => {
      const bindingMemberName = getBindingMemberName(employee.id);
      return [
        employee.name,
        employee.mobile,
        employee.staffno,
        bindingMemberName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(keyword);
    });
  }, [erpEmployeeQuery, erpEmployees, getBindingMemberName]);

  const boundCount = mappings.items?.length || 0;
  const pendingCount = Math.max(storeMembers.length - boundCount, 0);

  if (loading) {
    return (
      <View css="flex: 1; align-items: center; justify-content: center;">
        <ActivityIndicator size="large" color={Colors.Gray2} />
      </View>
    );
  }

  return (
    <ScrollView
      css="flex: 1; background-color: #f7f7f7;"
      keyboardShouldPersistTaps="handled"
      automaticallyAdjustKeyboardInsets
    >
      <View
        css={`
          background-color: #ffffff;
          border-radius: 16px;
          margin: 15px;
          padding: 16px;
        `}
      >
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: 16px;
            color: #101828;
          `}
        >
          {`${currentProviderName}员工绑定说明`}
        </Text>
        <Text css="font-size: 13px; color: #667085; line-height: 20px; margin-top: 8px;">
          请将门店员工与 ERP
          员工一一对应；订单同步时会依赖这里的绑定关系补齐服务顾问信息。
        </Text>
      </View>

      <View
        css={`
          flex-direction: row;
          justify-content: space-between;
          margin: 0 15px;
        `}
      >
        <StatsCard
          title="当前集成"
          value={currentProviderId ? currentProviderName : '未绑定'}
        />
        <StatsCard
          title="映射进度"
          value={`${boundCount}/${storeMembers.length || 0}`}
          valueColor={
            pendingCount === 0 ? StatusColors.Success : StatusColors.Warn
          }
        />
        <StatsCard
          title="待处理"
          value={String(pendingCount)}
          valueColor={
            pendingCount === 0 ? StatusColors.Success : StatusColors.Danger
          }
        />
      </View>

      {dirty && (
        <View
          css={`
            background-color: #fff7e8;
            border-radius: 14px;
            margin: 15px 15px 0 15px;
            padding: 12px 14px;
          `}
        >
          <Text css="font-size: 13px; color: #ad6800; line-height: 20px;">
            当前绑定关系有未保存的调整，返回前记得点“保存绑定关系”。
          </Text>
        </View>
      )}

      <View style={{ marginHorizontal: 15, marginTop: 15 }}>
        <SearchInput
          query={memberQuery}
          placeholder="搜索门店员工 / 已绑定 ERP 员工"
          onQueryChange={setMemberQuery}
          style={{
            backgroundColor: '#fff',
            borderWidth: 1,
            borderColor: '#e5e7eb',
          }}
        />
      </View>

      <TableView css="margin-top: 15px;">
        <Section title={`门店员工列表 (${filteredStoreMembers.length})`}>
          {filteredStoreMembers.map(member => {
            const boundErpEmp = getBoundEmployee(member.id);
            return (
              <Cell
                key={member.id}
                title={member.name}
                detail={boundErpEmp ? boundErpEmp.name : '未绑定'}
                detailTextStyle={{
                  color: boundErpEmp ? Colors.Gray1 : StatusColors.Warn,
                }}
                detailIcon={'disclosure'}
                onPress={() => {
                  setSelectorMemberId(member.id);
                }}
              />
            );
          })}
        </Section>
      </TableView>

      {!filteredStoreMembers.length ? (
        <View css="padding: 40px 20px; align-items: center; justify-content: center;">
          <Text css="color: #999; font-size: 15px;">没有匹配到门店员工</Text>
        </View>
      ) : null}

      {!storeMembers.length ? (
        <View css="padding: 24px 20px; align-items: center; justify-content: center;">
          <Text css="color: #999; font-size: 15px;">
            当前门店暂无可绑定员工
          </Text>
        </View>
      ) : null}

      {!erpEmployees.length ? (
        <View css="padding: 24px 20px; align-items: center; justify-content: center;">
          <Text css="color: #999; font-size: 15px; text-align: center; line-height: 22px;">
            {`当前${currentProviderName}暂无员工数据\n请先确认${currentProviderName}已成功登录或包含员工信息`}
          </Text>
        </View>
      ) : null}

      <View css="padding: 20px; align-items: center; margin-top: 10px; margin-bottom: 30px;">
        <Button
          title={
            saving ? '保存中...' : dirty ? '保存绑定关系' : '绑定关系已保存'
          }
          style={{
            width: '100%',
            backgroundColor: dirty ? '#5ba0e0' : Colors.Gray4,
            height: 48,
          }}
          textStyle={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}
          disabled={saving || !currentProviderId || !dirty}
          onPress={onSave}
        />
      </View>

      <Modal
        transparent
        animationType="fade"
        visible={selectorMemberId != null}
        onRequestClose={() => {
          setSelectorMemberId(undefined);
          setErpEmployeeQuery('');
        }}
      >
        <View
          css={`
            flex: 1;
            justify-content: flex-end;
            background-color: rgba(0, 0, 0, 0.35);
          `}
        >
          <TouchableOpacity
            activeOpacity={1}
            style={{ flex: 1 }}
            onPress={() => {
              setSelectorMemberId(undefined);
              setErpEmployeeQuery('');
            }}
          />
          <View
            css={`
              max-height: 78%;
              background-color: #fff;
              border-top-left-radius: 16px;
              border-top-right-radius: 16px;
              padding-bottom: 20px;
            `}
          >
            <View css="padding: 18px 20px 10px 20px;">
              <Text
                css={`
                  color: #333;
                  font-size: 18px;
                  font-family: ${FontFamily.NotoSans.Bold};
                `}
              >
                {`选择${currentProviderName}员工`}
              </Text>
              <Text css="color: #999; margin-top: 6px;">
                {selectedMember
                  ? `将 ${selectedMember.name} 绑定到${currentProviderName}员工`
                  : ''}
              </Text>
            </View>
            <View style={{ paddingHorizontal: 20, paddingBottom: 12 }}>
              <SearchInput
                query={erpEmployeeQuery}
                placeholder={`搜索${currentProviderName}员工 / 手机号`}
                onQueryChange={setErpEmployeeQuery}
                style={{
                  backgroundColor: '#f8fafc',
                  borderWidth: 1,
                  borderColor: '#e5e7eb',
                }}
              />
            </View>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              automaticallyAdjustKeyboardInsets
            >
              <TableView>
                <Cell
                  title="（不绑定）"
                  onPress={() => void updateMapping(selectorMemberId!, null)}
                  detailIcon={
                    !selectedMemberMapping?.erpEmployeeId ? (
                      <Icon name="check" size={20} color={'#5ba0e0'} />
                    ) : undefined
                  }
                />
                {filteredErpEmployees.map(employee => {
                  const selected =
                    selectedMemberMapping?.erpEmployeeId === employee.id;
                  const bindingMemberName = getBindingMemberName(employee.id);
                  const detailText =
                    bindingMemberName &&
                    bindingMemberName !== selectedMember?.name
                      ? `${
                          employee.mobile || employee.staffno || ''
                        } 已绑定 ${bindingMemberName}`.trim()
                      : employee.mobile || employee.staffno || '';
                  return (
                    <Cell
                      key={employee.id}
                      title={employee.name}
                      subTitle={employee.title || undefined}
                      detail={detailText}
                      detailTextStyle={{
                        color:
                          bindingMemberName &&
                          bindingMemberName !== selectedMember?.name
                            ? Colors.Orange2
                            : Colors.Gray2,
                      }}
                      onPress={() =>
                        void updateMapping(selectorMemberId!, employee.id)
                      }
                      detailIcon={
                        selected ? (
                          <Icon name="check" size={20} color={'#5ba0e0'} />
                        ) : undefined
                      }
                    />
                  );
                })}
              </TableView>
              {!filteredErpEmployees.length && (
                <View css="padding: 28px 20px; align-items: center;">
                  <Text css="font-size: 14px; color: #98a2b3;">
                    没有匹配到 ERP 员工
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
});

const StatsCard = memo(
  ({
    title,
    value,
    valueColor,
  }: {
    title: string;
    value: string;
    valueColor?: string;
  }) => {
    return (
      <View
        style={{
          width: '31.5%',
          backgroundColor: '#fff',
          borderRadius: 14,
          padding: 14,
        }}
      >
        <Text style={{ fontSize: 11, color: '#98a2b3', marginBottom: 6 }}>
          {title}
        </Text>
        <Text
          style={{
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: 16,
            color: valueColor || '#101828',
          }}
          numberOfLines={1}
        >
          {value}
        </Text>
      </View>
    );
  },
);
